// @flow

import React from 'react';
import {
  ButtonBase,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  Menu,
  Typography,
} from '@material-ui/core';

import {Close} from '@material-ui/icons';
import {colors} from '../../../../../../../assets/styles/colors';
import type {Theme} from '@material-ui/core';
import {useConversationTags} from '../../../../utils/conversationTag';
import {
  useFilterConversationsActions,
  useFilterConversations,
} from '../../../../utils/conversation';
import {useCollaborators} from '../../../../../../configure/accounts/utils';
import FilterRemove from '../../../../../../../elements/icons/FilterRemove';

type Props = {
  anchorEl?: null | HTMLElement,
  setAnchorEl: (null | HTMLElement) => void,
};

const useStyles = makeStyles((theme: Theme) => ({
  menu: {
    marginTop: '4rem',
    borderRadius: '12px',
    '&.MuiPaper-root': {
      maxWidth: 'unset',
      width: 190,
    },
  },
  menuContent: {
    width: 190,
    padding: '.75rem',
    position: 'relative',
  },
  menuTitle: {
    fontWeight: 500,
    color: colors.grey900,
    fontSize: 14,
  },
  greyText: {
    color: colors.grey700,
  },
  filterList: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  controlLabel: {
    marginLeft: 0,
    marginRight: 0,
  },
  checkbox: {
    padding: theme.spacing(0, 1, 0, 0),
  },
  closeButton: {
    position: 'absolute',
    right: 5,
    top: 0,
  },
  itemsWrap: {
    width: '100%',
  },
  clearFilterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    color: colors.primary,
    fontSize: '14px',
    width: '100%',
    padding: '.75rem',
    borderRadius: '2rem',
  },
  clearIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
  },
  buttonLabel: {
    fontWeight: 500,
    fontSize: '14px',
    color: colors.primary,
  },
}));

export default function FilterMenu(props: Props) {
  const {anchorEl, setAnchorEl} = props;
  const classes = useStyles();

  const {conversationTags} = useConversationTags();
  const {
    filterByTags,
    filterByCollaborator,
    filterByAdmin,
    clearFilters,
  } = useFilterConversationsActions();
  const {
    tags,
    collaborators: collaboratorFilter,
    admin,
  } = useFilterConversations();

  const {collaborators} = useCollaborators();

  const toggleAdminFilter = () => {
    filterByAdmin(!admin);
  };

  return (
    <Menu
      id="filter-menu"
      anchorEl={anchorEl}
      open={!!anchorEl}
      className={classes.menu}
      onClose={() => setAnchorEl(null)}
      MenuListProps={{
        'aria-labelledby': 'filter-menu',
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
    >
      <div className={classes.menuContent}>
        <IconButton
          size="small"
          className={classes.closeButton}
          aria-label="close"
          onClick={() => setAnchorEl(null)}
        >
          <Close />
        </IconButton>
        <Typography className={classes.greyText}>Filtrar por: </Typography>
        <div className={classes.itemsWrap}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div className={classes.filterList}>
                <FormControlLabel
                  classes={{
                    root: classes.controlLabel,
                    label: classes.greyText,
                  }}
                  control={
                    <Checkbox
                      onClick={toggleAdminFilter}
                      classes={{root: classes.checkbox}}
                      color="primary"
                      checked={!!admin}
                    />
                  }
                  label="Administrador"
                />
              </div>
            </Grid>
            {collaborators.length > 0 && (
              <Grid item xs={12}>
                <Typography className={classes.menuTitle}>
                  Colaborador
                </Typography>
                <div className={classes.filterList}>
                  {collaborators.map(collaborator => {
                    const checked =
                      Array.isArray(collaboratorFilter) &&
                      collaboratorFilter.includes(collaborator.id);

                    const handleClick = () => {
                      const prevCollaborators = Array.isArray(
                        collaboratorFilter
                      )
                        ? collaboratorFilter
                        : [];
                      if (checked) {
                        filterByCollaborator(
                          prevCollaborators.filter(c => c !== collaborator.id)
                        );
                      } else {
                        filterByCollaborator([
                          ...prevCollaborators,
                          collaborator.id,
                        ]);
                      }
                    };

                    return (
                      <FormControlLabel
                        key={collaborator.id}
                        classes={{
                          root: classes.controlLabel,
                          label: classes.greyText,
                        }}
                        control={
                          <Checkbox
                            classes={{root: classes.checkbox}}
                            color="primary"
                            checked={checked}
                            onClick={handleClick}
                          />
                        }
                        label={collaborator.firstName}
                      />
                    );
                  })}
                </div>
              </Grid>
            )}
            {conversationTags.length > 0 && (
              <Grid item xs={12}>
                <Typography className={classes.menuTitle}>Etiqueta</Typography>
                <div className={classes.filterList}>
                  {conversationTags.map(tag => {
                    const exist = Array.isArray(tags) && tags.includes(tag.id);

                    return (
                      <FormControlLabel
                        key={tag.id}
                        classes={{
                          root: classes.controlLabel,
                          label: classes.greyText,
                        }}
                        control={
                          <Checkbox
                            classes={{root: classes.checkbox}}
                            checked={exist}
                            color="primary"
                            onClick={() => {
                              const prevTags = Array.isArray(tags) ? tags : [];
                              if (exist) {
                                filterByTags(
                                  prevTags.filter(t => t !== tag.id)
                                );
                              } else {
                                filterByTags([...prevTags, tag.id]);
                              }
                            }}
                          />
                        }
                        label={tag.tag}
                      />
                    );
                  })}
                  {conversationTags.length === 0 && (
                    <Typography className={classes.greyText}>
                      No hay etiquetas
                    </Typography>
                  )}
                </div>
              </Grid>
            )}
            <Grid item xs={12}>
              <ButtonBase
                className={classes.clearFilterButton}
                onClick={clearFilters}
              >
                <span className={classes.clearIcon}>
                  <FilterRemove />
                </span>
                <Typography className={classes.buttonLabel}>
                  Limpiar filtros
                </Typography>
              </ButtonBase>
            </Grid>
          </Grid>
        </div>
      </div>
    </Menu>
  );
}

FilterMenu.propTypes = {};
